import { usePrazosDaOs } from '../../../api/queries/ordensDeServico'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Explicacao } from '../../../components/Explicacao'
import { Cartao } from '../../../components/ListaDeDados'
import { cn } from '../../../lib/cn'
import { dataSimples } from '../../../lib/datas'
import type { PrazoDaOs } from '../../../types/ordemDeServico'
import { TERMOS_DA_OS } from '../termosDaOs'
import { OS } from '../textosDaOs'

/**
 * Os prazos do ciclo de recebimento da ordem.
 *
 * **"Não apurável" é resultado de primeira classe, com a razão por extenso — nunca vazio, nunca
 * zero, nunca "vencido".** Sem a entrega ao preposto registrada (6.24.6, p.45) o prazo não começou
 * a correr, e escrever "vencido" ali acusaria a contratada de um atraso que ninguém tem como
 * contar. É a mesma disciplina do indicador não apurado do fechamento: zero é o resultado de quem
 * cumpriu a meta, e "não apurado" é o de quem não tem como saber.
 *
 * **O lado do contrato aparece em cada prazo.** TRP e TRD são atos da Prefeitura (8.17 e 8.27,
 * p.57): numa tela que também mostra glosa e pontos de IAD, um prazo vencido sem dono seria lido
 * como falta da contratada — e viraria desconto na fatura de quem não tinha como cumpri-lo.
 *
 * @param props o identificador da ordem
 * @returns o cartão dos prazos
 */
export function PrazosDaOs({ id }: { id: number }) {
  const { data: prazos, isPending, isError, error } = usePrazosDaOs(id)

  if (isPending) {
    return (
      <Cartao titulo={OS.prazos}>
        <Esqueleto linhas={3} />
      </Cartao>
    )
  }
  if (isError) {
    return <AvisoDeErro erro={error} titulo="Não foi possível calcular os prazos" />
  }

  return (
    <Cartao titulo={OS.prazos} descricao="O que o contrato conta a partir dos atos registrados">
      {/* Os dois marcos de que TODO prazo desta ordem depende. Eles vêm antes da lista porque, sem
          o primeiro, nenhum prazo abaixo existe — e o segundo é o que o contrato cita como início
          do recebimento provisório sem trazer o modelo do documento. */}
      <p className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1">
          {TERMOS_DA_OS.entregaAoPreposto.rotulo}
          <Explicacao
            titulo={TERMOS_DA_OS.entregaAoPreposto.rotulo}
            texto={TERMOS_DA_OS.entregaAoPreposto.ajuda}
            className="h-3.5 w-3.5"
          />
        </span>
        <span className="inline-flex items-center gap-1">
          {TERMOS_DA_OS.termoDeEncerramentoDaOs.rotulo}
          <Explicacao
            titulo={TERMOS_DA_OS.termoDeEncerramentoDaOs.rotulo}
            texto={TERMOS_DA_OS.termoDeEncerramentoDaOs.ajuda}
            className="h-3.5 w-3.5"
          />
        </span>
      </p>

      <ul className="space-y-3">
        {prazos.map((prazo) => (
          <li key={prazo.deQue} className="rounded-lg border border-slate-200 p-3">
            <Prazo prazo={prazo} />
          </li>
        ))}
      </ul>
    </Cartao>
  )
}

/** Um prazo: com data, ou com a razão de não haver data. */
function Prazo({ prazo }: { prazo: PrazoDaOs }) {
  const daPrefeitura = prazo.contra === 'CONTRATANTE'
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">{prazo.rotulo}</h3>
        {prazo.apuravel && prazo.vence ? (
          <span className="whitespace-nowrap rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-900 ring-1 ring-inset ring-sky-200">
            {OS.vence} {dataSimples(prazo.vence)}
          </span>
        ) : (
          <span
            className={cn(
              'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
              'bg-slate-100 text-slate-800 ring-slate-300',
            )}
          >
            {OS.prazoNaoApuravel.titulo}
          </span>
        )}
      </div>

      {!prazo.apuravel && prazo.porQueNaoApuravel && (
        <p className="mt-1 max-w-prose text-sm text-slate-700">{prazo.porQueNaoApuravel}</p>
      )}

      <p className="mt-1 text-xs text-slate-500">
        {prazo.fonteDoTr}
        {prazo.seloDoParametro && prazo.seloDoParametro !== 'FORMALIZADO' && (
          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">
            Critério {prazo.seloDoParametro.toLowerCase()}
          </span>
        )}
      </p>

      {daPrefeitura && <p className="mt-1 max-w-prose text-xs text-slate-600">{OS.prazoDaPrefeitura}</p>}
    </>
  )
}
